// components/TakeoffEstimatePage.jsx
// The ESTIMATE as a full page — shared by Project takeoffs and Opportunity (bid-stage) takeoffs.
// Traced assemblies (conditions) → items from the item database → qty / hours / cost → client price.
// All math comes from lib/takeoff/estimateCalc.js, so the numbers match what the server publishes.
//
// Props (same as before + a few new, all optional):
//   conditions, condMetrics, mappings, setMappings, getCatalog, status, knownBidAreas
//   settings, setSettings      — overhead / profit / contingency / tax / crew (saved on the takeoff)
//   ownerLabel, ownerKind      — "Elm St Hospital" / "project" | "opportunity"
//   onSaveDraft, onSaveAndPush (project only), onAddToChangeOrder, onBack
//   onAutoSave, autoSave       — changes save by themselves a moment after you stop editing (one assembly at a time)
//   templateApi                — standard assemblies { list, save, remove, duplicate, used }
//   getCostCodes               — () => [{ _id, code, name }] for the cost-code breakdown
//   reportMeta                 — { company, project, drawing, client, preparedBy, estimateNo } defaults for the print
//   takeoffId, ownerId, apiBase — vendor price requests on the Material take-list (components/VendorPricing.jsx)

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  DEFAULT_SETTINGS,
  rollup,
  toCoItem,
  resolveDefaultRate,
} from "../lib/takeoff/estimateCalc";
import {
  costCodeIndex,
  costCodeBreakdown,
  resolveItemCostCode,
  buildReportHtml,
  openReportWindow,
  fillReportWindow,
  reportWindowStatus,
  rowBasis,
} from "../lib/takeoff/estimateReport";
import { computeCondMetrics } from "./TakeoffViewer";
import { buildSheetLegends } from "../lib/takeoff/sheetLegend";
import {
  ItemSearchModal,
  WasteModal,
  LibraryModal,
  PrintModal,
  Menu,
  AssemblyPicker,
  MODES,
  itemFromCatalog,
  expandChain,
  applyLink,
  toTemplateItem,
} from "./TakeoffEstimateTools";
import VendorPricing from "./VendorPricing";

const nz = (v) => (Math.abs(Number(v)) < 1e-9 ? 0 : Number(v)); // never "-0"
const n0 = (v) =>
  v == null ? "—" : nz(v).toLocaleString("en-US", { maximumFractionDigits: 0 });
const n1 = (v, d = 1) =>
  v == null
    ? "—"
    : nz(v).toLocaleString("en-US", {
        minimumFractionDigits: d,
        maximumFractionDigits: d,
      });
const money = (v) =>
  v == null
    ? "—"
    : Number(v).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      });
const money2 = (v) =>
  v == null
    ? "—"
    : Number(v).toLocaleString("en-US", { style: "currency", currency: "USD" });
const uid = () =>
  Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
const cls = (...c) => c.filter(Boolean).join(" ");

const MATERIAL = "#f97316"; // orange
const LABOUR = "#38bdf8"; // sky

// wall height: the condition's own, else the drawn assembly's, else "WALL HEIGHT 9'-0"" in the notes
function effectiveHeight(cond, assemblies = []) {
  const h =
    Number(cond.height) ||
    Number(
      assemblies.find(
        (x) => x.id === cond.assemblyId || x.conditionId === cond.id,
      )?.height,
    );
  if (h > 0) return h;
  const m = String(cond.description || "").match(
    /HEIGHT\s*(\d+)\s*['′]\s*-?\s*(\d+)?/i,
  );
  return m ? Number(m[1]) + (Number(m[2]) || 0) / 12 : 0;
}

// ── donut ───────────────────────────────────────────────────────────────────
function Donut({ parts, size = 190, thickness = 26, center }) {
  const total = parts.reduce((s, p) => s + p.value, 0);
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  let acc = 0;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="#1f2937"
          strokeWidth={thickness}
        />
        {total > 0 &&
          parts.map((p) => {
            const len = (p.value / total) * c;
            const el = (
              <circle
                key={p.label}
                cx={size / 2}
                cy={size / 2}
                r={r}
                fill="none"
                stroke={p.color}
                strokeWidth={thickness}
                strokeDasharray={`${Math.max(len - 2, 0)} ${c - Math.max(len - 2, 0)}`}
                strokeDashoffset={-acc}
                transform={`rotate(-90 ${size / 2} ${size / 2})`}
              >
                <title>{`${p.label}: ${money(p.value)} (${((p.value / total) * 100).toFixed(1)}%)`}</title>
              </circle>
            );
            acc += len;
            return el;
          })}
      </svg>
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center">
        {center}
      </div>
    </div>
  );
}

const inp =
  "w-full rounded-md border border-gray-700 bg-gray-950 px-1.5 py-1 text-[12px] text-gray-100 placeholder-gray-600 focus:border-orange-500 focus:outline-none";

function Field({ label, children, className }) {
  return (
    <label className={cls("block", className)}>
      <span className="mb-0.5 block text-[9.5px] font-semibold uppercase tracking-wider text-gray-500">
        {label}
      </span>
      {children}
    </label>
  );
}

// ── one assembly item ───────────────────────────────────────────────────────
function ItemRow({
  row,
  cond,
  onChange,
  onRemove,
  refNo,
  selected = false,
  onSelect, // (checked, shiftKey) => void
  ccLabel = "", // resolved cost code "09 21 16 – Gypsum board"
  ccOptions = [], // [{ id, code, name }]
  chainedFrom = "", // the item that brought this one in
}) {
  const it = row.item;
  const isL = it.category === "L";
  const [open, setOpen] = useState(false);
  const set = (k, v) => onChange({ [k]: v });
  const num = (k) => (e) =>
    set(k, e.target.value === "" ? "" : Number(e.target.value));
  const mode = it.calcMode || "auto";

  // "420 SF ÷ 42 SF = 10 pcs"  /  "73 studs × 12.1′ = 885 LF"
  const basis = rowBasis(row);

  return (
    <div className="border-t border-white/5">
      <div
        onClick={(e) => {
          // clicking the row toggles its settings; controls inside it don't
          if (e.target.closest("input,select,button,textarea,a")) return;
          setOpen((v) => !v);
        }}
        className={cls(
          "grid cursor-pointer grid-cols-12 items-center gap-2 px-3 py-2 transition-colors hover:bg-white/[0.03]",
          open && "bg-white/[0.04]",
        )}
      >
        <div className="col-span-12 flex min-w-0 items-center gap-2 md:col-span-4">
          {onSelect && (
            <input
              type="checkbox"
              checked={selected}
              onChange={(e) =>
                onSelect(e.target.checked, e.nativeEvent?.shiftKey)
              }
              title="Select (copy / paste / waste % / delete several at once)"
              className="h-3.5 w-3.5 shrink-0 cursor-pointer accent-orange-500"
            />
          )}
          <span className="w-3 shrink-0 text-[10px] text-gray-500">
            {open ? "▾" : "▸"}
          </span>
          <span
            className="h-2 w-2 shrink-0 rounded-full"
            style={{ background: isL ? LABOUR : MATERIAL }}
          />
          {refNo != null && (
            <span className="shrink-0 rounded bg-white/5 px-1.5 py-px font-mono text-[10px] text-gray-400">
              #{String(refNo).padStart(3, "0")}
            </span>
          )}
          <div className="min-w-0">
            <div className="truncate text-[13px] font-medium text-gray-100">
              {it.title || it.itemCode}
            </div>
            <div className="truncate text-[10.5px] text-gray-500">
              {it.itemCode} · {it.uom}
              {it.size ? ` · ${it.size}` : ""}
              {ccLabel && (
                <span
                  className="ml-1.5 rounded bg-violet-500/15 px-1 py-px text-[9.5px] text-violet-300"
                  title="Cost code"
                >
                  {ccLabel.split(" – ")[0]}
                </span>
              )}
              {chainedFrom && (
                <span
                  className="ml-1.5 text-[9.5px] text-amber-300/80"
                  title={`Came in with ${chainedFrom}`}
                >
                  ⛓ {chainedFrom}
                </span>
              )}
              {Number(it.wastePct) > 0 && (
                <span className="ml-1.5 text-[9.5px] text-amber-300/80">
                  +{it.wastePct}% waste
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="col-span-5 md:col-span-3">
          <div
            className={cls(
              "truncate text-[11.5px]",
              row.qty === null ? "text-red-300" : "text-gray-300",
            )}
            title={basis}
          >
            {basis || "—"}
          </div>
          <div className="text-[10px] text-gray-600">
            {MODES.find((m) => m[0] === mode)?.[1] || mode}
          </div>
        </div>
        <div className="col-span-3 text-right md:col-span-1">
          <div className="text-[13px] font-semibold text-gray-100">
            {row.qty === null ? "—" : n1(row.qty, row.qty % 1 ? 1 : 0)}
          </div>
          <div className="text-[10px] text-gray-500">{row.unit}</div>
          {row.total != null && !isL && (
            <div className="text-[10px] text-sky-300/80">
              {n0(row.total)} {row.totalUnit}
            </div>
          )}
        </div>
        <div className="col-span-4 text-right md:col-span-1">
          {isL ? (
            <>
              <div className="text-[12px] text-sky-300">
                {row.hours != null ? `${n1(row.hours)} h` : "—"}
              </div>
              <div className="text-[10px] text-gray-600">
                {Number(it.productionRate) > 0
                  ? `${it.productionRate}/hr`
                  : "no rate"}
              </div>
            </>
          ) : null}
        </div>
        <div className="col-span-5 flex items-center gap-1 md:col-span-1">
          <span className="text-[10px] text-gray-500">$</span>
          <input
            type="number"
            step="0.01"
            value={it.rate ?? ""}
            onChange={num("rate")}
            className={cls(inp, "text-right")}
          />
        </div>
        <div className="col-span-5 text-right md:col-span-1">
          <div className="text-[13px] font-semibold text-white">
            {row.amount == null ? "—" : money(row.amount)}
          </div>
        </div>
        <div className="col-span-2 flex justify-end gap-1 md:col-span-1">
          <button
            onClick={onRemove}
            title="Delete item"
            className="rounded-md bg-white/5 px-1.5 py-1 text-gray-400 hover:bg-red-500/20 hover:text-red-300"
          >
            <svg
              viewBox="0 0 24 24"
              className="h-3.5 w-3.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14M10 11v6M14 11v6" />
            </svg>
          </button>
        </div>
      </div>
      {open && (
        <div className="grid grid-cols-3 gap-2 border-t border-white/5 bg-black/20 px-3 py-3 md:grid-cols-6 lg:grid-cols-9">
          <Field label="Quantity basis" className="col-span-3 md:col-span-2">
            <select
              value={mode}
              onChange={(e) => set("calcMode", e.target.value)}
              className={inp}
            >
              {MODES.map(([k, l]) => (
                <option key={k} value={k}>
                  {l}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Size" className="col-span-2 md:col-span-1">
            <input
              value={it.size || ""}
              onChange={(e) =>
                // a new size means a new coverage — drop any hand-typed override
                onChange({ size: e.target.value, covManual: false })
              }
              placeholder={`4'x8'  134.5"  145-1/2"`}
              className={inp}
            />
          </Field>
          <Field
            label={
              row.covSource === "size"
                ? "Coverage / pc · auto"
                : "Coverage / pc · manual"
            }
            className="col-span-2"
          >
            <div className="flex items-center gap-1">
              <input
                type="number"
                step="any"
                value={
                  row.covSource === "size" && row.covUsed != null
                    ? Math.round(row.covUsed * 1000) / 1000
                    : (it.coveragePerUnit ?? "")
                }
                onChange={(e) =>
                  onChange({
                    coveragePerUnit:
                      e.target.value === "" ? "" : Number(e.target.value),
                    covManual: e.target.value !== "",
                  })
                }
                placeholder="from size"
                className={cls(inp, row.covSource === "size" && "text-sky-300")}
              />
              <span className="shrink-0 text-[10px] text-gray-500">
                {row.covUnit || ""}
              </span>
              {it.covManual && (
                <button
                  onClick={() => onChange({ covManual: false })}
                  title="Calculate the coverage from the size again"
                  className="shrink-0 rounded bg-white/5 px-1.5 py-1 text-[10px] text-orange-300 hover:bg-white/10"
                >
                  Auto
                </button>
              )}
            </div>
          </Field>
          {it.covManual && (
            <Field label="Cov. unit">
              <select
                value={it.covUnit || ""}
                onChange={(e) => set("covUnit", e.target.value)}
                className={inp}
              >
                <option value="">auto</option>
                <option value="SF">SF</option>
                <option value="LF">LF</option>
              </select>
            </Field>
          )}
          {mode === "oc" && (
            <Field label="Direction">
              <select
                value={it.ocDir === "h" ? "h" : "v"}
                onChange={(e) => set("ocDir", e.target.value)}
                className={inp}
              >
                <option value="v">Vertical (studs)</option>
                <option value="h">Horizontal (rows)</option>
              </select>
            </Field>
          )}
          {mode === "oc" && (
            <Field label="O.C. (in)">
              <input
                type="number"
                value={it.ocInches ?? ""}
                onChange={num("ocInches")}
                placeholder="16"
                className={inp}
              />
            </Field>
          )}
          <Field label="Layers">
            <input
              type="number"
              min="1"
              value={it.layers ?? 1}
              onChange={num("layers")}
              className={inp}
            />
          </Field>
          <Field label="Multiplier">
            <input
              type="number"
              step="any"
              value={it.multiplier ?? 1}
              onChange={num("multiplier")}
              className={inp}
            />
          </Field>
          <Field label="Waste %">
            <input
              type="number"
              step="any"
              value={it.wastePct ?? 0}
              onChange={num("wastePct")}
              className={inp}
            />
          </Field>
          {ccOptions.length > 0 && (
            <Field label="Cost code" className="col-span-3 md:col-span-2">
              <select
                value={it.costCode || ""}
                onChange={(e) => {
                  const c = ccOptions.find((o) => o.id === e.target.value);
                  onChange({
                    costCode: c ? c.id : null,
                    costCodeLabel: c
                      ? [c.code, c.name].filter(Boolean).join(" – ")
                      : "",
                  });
                }}
                className={inp}
              >
                <option value="">
                  {ccLabel && !it.costCode
                    ? `From item: ${ccLabel}`
                    : "— none —"}
                </option>
                {ccOptions.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.code}
                    {o.name ? ` – ${o.name}` : ""}
                  </option>
                ))}
              </select>
            </Field>
          )}
          {isL && (
            <Field label="Production (units/hr)">
              <input
                type="number"
                step="any"
                value={it.productionRate ?? ""}
                onChange={num("productionRate")}
                className={inp}
              />
            </Field>
          )}
          {row.pricedPerCov && (
            <div className="col-span-3 self-end text-[10.5px] text-orange-300/80 md:col-span-3">
              Rate is per {row.covUnit} → each piece = {n1(row.covUsed, 0)}{" "}
              {row.covUnit} × {money2(it.rate)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
export default function TakeoffEstimatePage({
  conditions = [],
  condMetrics = {},
  condMetricsByArea = null, // { [condId]: { [bidArea]: metrics } } from the sheets' bid areas
  assemblies = [],
  shapes = [], // the drawing's lines / areas / counts (for the printed sheet legend)
  scales = {},
  pageNames = {},
  excludedPages = [],
  pageBidAreas = {},
  units = [],
  renderSheetImage = null, // (page, { shapes, conditions }) => Promise<dataURL> — drawn by the viewer
  mappings = [],
  setMappings,
  printDrawings = null, // (pages[]) => void — the drawing's working-drawings PDF (same as the takeoff's Print)
  getCatalog,
  status = "draft",
  knownBidAreas = [],
  settings: settingsProp,
  setSettings,
  ownerLabel = "",
  ownerKind = "project",
  onSaveDraft,
  onSaveAndPush,
  onAddToChangeOrder,
  onRenameCondition, // (condId, newName) — renames the assembly everywhere
  onBack,
  onAutoSave, // () => Promise — quiet save of the estimate's changes (host decides what to send)
  autoSave = false,
  templateApi = null,
  getCostCodes = null,
  reportMeta = {},
  fullscreen = false,
  onToggleFullscreen, // the estimate full screen (on its own; with the drawing full screen it already is)
  takeoffId = null, // for vendor price requests (Material take-list)
  ownerId = null,
  apiBase = "/api",
}) {
  const [localSettings, setLocalSettings] = useState(DEFAULT_SETTINGS);
  const settings = settingsProp || localSettings;
  const patchSettings = (p) => {
    record("s:" + Object.keys(p).join(","));
    const next = { ...setRef.current, ...p };
    setRef.current = next;
    (setSettings || setLocalSettings)(next);
  };

  const [tab, setTab] = useState("assemblies");
  const [catalog, setCatalog] = useState([]);
  const [costCodes, setCostCodes] = useState([]); // [{ _id, code, name }] — company cost codes
  const [catLoading, setCatLoading] = useState(false);
  const [searchFor, setSearchFor] = useState(null);
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [areaFilter, setAreaFilter] = useState("all");
  const [groupBy, setGroupBy] = useState("area"); // "area" | "assembly"
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState(null); // {ok, text}
  const [savedAt, setSavedAt] = useState(null);
  const loaded = useRef(false);

  // ── undo / redo (assembly items + estimate settings) ─────────────────────
  const mapRef = useRef(mappings);
  mapRef.current = mappings;
  const setRef = useRef(settings);
  setRef.current = settings;
  const [past, setPast] = useState([]);
  const [future, setFuture] = useState([]);
  const lastEdit = useRef({ key: "", t: 0 });
  const writeSettings = (v) => (setSettings || setLocalSettings)(v);
  const record = (key) => {
    const now = Date.now();
    const same =
      key && lastEdit.current.key === key && now - lastEdit.current.t < 1200;
    lastEdit.current = { key, t: now };
    if (same) return; // typing in one field = one undo step
    setPast((p) => [
      ...p.slice(-49),
      { mappings: mapRef.current, settings: setRef.current },
    ]);
    setFuture([]);
  };
  // every change made HERE goes through localSet; anything else that changes `mappings` came from someone
  // else (merged in by the host) — see the effect below that keeps undo from taking their work back out
  const localRef = useRef(mappings);
  const localSet = (next) => {
    localRef.current = next;
    mapRef.current = next;
    setMappings(next);
  };

  // vendor prices chosen on the Material take-list → each matching material item's rate + vendor (one undo step)
  // awards: [{ itemCode, size, vendorId, vendorName, price, sku, quoteId, source }]
  const applyVendorPrices = (awards, vendorPricing) => {
    record("vendor-prices");
    const byKey = new Map(
      awards.map((a) => [`${a.itemCode}||${a.size || ""}`, a]),
    );
    const at = new Date().toISOString();
    const next = mapRef.current.map((m) => {
      let hit = false;
      const items = (m.items || []).map((it) => {
        if (it.category === "L") return it;
        const a = byKey.get(`${it.itemCode}||${it.size || ""}`);
        if (!a) return it;
        hit = true;
        return {
          ...it,
          rate: Number(a.price) || 0,
          vendorId: a.vendorId || null,
          vendorName: a.vendorName || "",
          vendorSku: a.sku || "",
          priceSource: a.source || "vendor quote",
          priceQuoteId: a.quoteId || null,
          priceAt: at,
        };
      });
      return hit ? { ...m, items } : m;
    });
    localSet(next);
    if (vendorPricing) {
      const ns = { ...setRef.current, vendorPricing };
      setRef.current = ns;
      writeSettings(ns);
    }
  };

  const commitMappings = (fn, key = "") => {
    const next = fn(mapRef.current);
    if (next === mapRef.current) return;
    record(key);
    localSet(next);
  };
  const restore = (snap) => {
    setRef.current = snap.settings;
    localSet(snap.mappings);
    writeSettings(snap.settings);
    lastEdit.current = { key: "", t: 0 };
  };
  const undo = () => {
    if (!past.length) return;
    const snap = past[past.length - 1];
    setPast(past.slice(0, -1));
    setFuture((f) => [
      ...f,
      { mappings: mapRef.current, settings: setRef.current },
    ]);
    restore(snap);
  };
  const redo = () => {
    if (!future.length) return;
    const snap = future[future.length - 1];
    setFuture(future.slice(0, -1));
    setPast((p) => [
      ...p,
      { mappings: mapRef.current, settings: setRef.current },
    ]);
    restore(snap);
  };
  const keys = useRef({});
  keys.current = { undo, redo };
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.metaKey || e.ctrlKey) || e.altKey) return;
      const k = e.key.toLowerCase();
      if (k !== "z" && k !== "y") return;
      const t = e.target;
      // leave native undo alone inside free-text boxes (names, notes)
      if (
        t &&
        (t.tagName === "TEXTAREA" ||
          (t.tagName === "INPUT" &&
            !["number", "checkbox", "range"].includes(t.type)))
      )
        return;
      e.preventDefault();
      if (k === "y" || e.shiftKey) keys.current.redo();
      else keys.current.undo();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // someone else's estimate changes arrived (the host merged them in): undo / redo must not undo THEIR work —
  // put their version of those assemblies into every undo snapshot
  useEffect(() => {
    if (mappings === localRef.current) return;
    const before = new Map(
      (localRef.current || []).map((m) => [m.conditionId, m]),
    );
    const after = new Map((mappings || []).map((m) => [m.conditionId, m]));
    const changed = new Set();
    for (const [cid, m] of after) if (before.get(cid) !== m) changed.add(cid);
    for (const cid of before.keys()) if (!after.has(cid)) changed.add(cid);
    localRef.current = mappings;
    if (!changed.size) return;
    const patch = (snapMaps) => {
      const keep = (snapMaps || []).filter((m) => !changed.has(m.conditionId));
      return [
        ...keep,
        ...[...changed].map((cid) => after.get(cid)).filter(Boolean),
      ];
    };
    setPast((p) => p.map((x) => ({ ...x, mappings: patch(x.mappings) })));
    setFuture((f) => f.map((x) => ({ ...x, mappings: patch(x.mappings) })));
  }, [mappings]);

  // stable reference numbers: give every item without one the next free number
  useEffect(() => {
    const all = mappings.flatMap((m) => m.items || []);
    if (!all.some((i) => !i.ref)) return;
    let n = Math.max(0, ...all.map((i) => Number(i.ref) || 0));
    const next = mappings.map((m) => ({
      ...m,
      items: (m.items || []).map((i) => (i.ref ? i : { ...i, ref: ++n })),
    }));
    localSet(next); // not an undo step
  }, [mappings]); // eslint-disable-line

  const [confirmDel, setConfirmDel] = useState(null); // {cid, iid, title}

  useEffect(() => {
    if (loaded.current || !getCatalog) return;
    loaded.current = true;
    setCatLoading(true);
    getCatalog()
      .then((r) => setCatalog(Array.isArray(r) ? r : []))
      .catch((e) => console.error("Catalog load failed", e))
      .finally(() => setCatLoading(false));
  }, [getCatalog]);

  const catalogById = useMemo(
    () => new Map(catalog.map((c) => [String(c._id), c])),
    [catalog],
  );
  const ccIndex = useMemo(() => costCodeIndex(costCodes), [costCodes]);
  const ccOptions = useMemo(
    () =>
      [...ccIndex.values()].sort((a, b) =>
        String(a.code).localeCompare(String(b.code), undefined, {
          numeric: true,
        }),
      ),
    [ccIndex],
  );

  // a linear condition with no height of its own borrows the assembly / notes height, so wall SF resolves
  const { effConds, effMetrics, effByArea } = useMemo(() => {
    const ec = [];
    const em = { ...condMetrics };
    const ea = condMetricsByArea ? {} : null;
    const fill = (m, h) =>
      m && !m.uncal && !(m.sf > 0) && m.lf > 0 ? { ...m, sf: m.lf * h } : m;
    for (const c of conditions) {
      const h = effectiveHeight(c, assemblies);
      const lin = c.type === "linear" && !(Number(c.height) > 0) && h > 0;
      ec.push(lin ? { ...c, height: h } : c);
      if (lin) em[c.id] = fill(condMetrics[c.id], h);
      if (ea && condMetricsByArea[c.id]) {
        ea[c.id] = {};
        for (const [k, m] of Object.entries(condMetricsByArea[c.id]))
          ea[c.id][k] = lin ? fill(m, h) : m;
      }
    }
    return { effConds: ec, effMetrics: em, effByArea: ea };
  }, [conditions, condMetrics, condMetricsByArea, assemblies]);

  const R = useMemo(
    () =>
      rollup({
        conditions: effConds,
        metrics: effMetrics,
        metricsByArea: effByArea,
        mappings,
        settings,
      }),
    [effConds, effMetrics, effByArea, mappings, settings],
  );

  const areas = useMemo(() => {
    const s = new Set(knownBidAreas);
    mappings.forEach((m) => m.bidArea && s.add(m.bidArea));
    R.blocks.forEach((b) => b.area && s.add(b.area));
    return [...s].sort();
  }, [knownBidAreas, mappings, R]);
  const bySheetAreas = R.blocks.some((b) => b.key.split("::")[1]);

  const blocks =
    areaFilter === "all"
      ? R.blocks
      : R.blocks.filter((b) => (b.area || "") === areaFilter);

  // blocks grouped under their bid area, each with its own subtotal
  const groups = useMemo(() => {
    if (groupBy !== "area") return [{ name: null, blocks }];
    const m = new Map();
    blocks.forEach((b) => {
      const k = b.area || "";
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(b);
    });
    return [...m.entries()]
      .sort(([a], [b]) => (a === "" ? 1 : b === "" ? -1 : a.localeCompare(b)))
      .map(([name, bl]) => ({
        name: name || "No bid area",
        unassigned: name === "",
        blocks: bl,
        material: bl.reduce((t, x) => t + x.material, 0),
        labour: bl.reduce((t, x) => t + x.labour, 0),
        hours: bl.reduce((t, x) => t + x.hours, 0),
        total: bl.reduce((t, x) => t + x.total, 0),
      }));
  }, [blocks, groupBy]);

  // ── mapping edits ─────────────────────────────────────────────────────────
  const getMapping = (cid) => mappings.find((m) => m.conditionId === cid);
  const updateMapping = (cid, patch, key = "") =>
    commitMappings((prev) => {
      const ex = prev.find((m) => m.conditionId === cid);
      if (ex)
        return prev.map((m) =>
          m.conditionId === cid ? { ...m, ...patch } : m,
        );
      return [
        ...prev,
        { conditionId: cid, bidArea: "", notes: "", items: [], ...patch },
      ];
    }, key);
  const updateItem = (cid, iid, patch) => {
    const mp = getMapping(cid);
    if (!mp) return;
    updateMapping(
      cid,
      {
        items: (mp.items || []).map((i) =>
          i.id === iid ? { ...i, ...patch } : i,
        ),
      },
      `i:${iid}:${Object.keys(patch).join(",")}`,
    );
  };
  const removeItem = (cid, iid) => {
    const mp = getMapping(cid);
    if (!mp) return;
    const gone = (mp.items || []).find((i) => i.id === iid);
    updateMapping(cid, { items: (mp.items || []).filter((i) => i.id !== iid) });
    setMsg({
      ok: true,
      text: `Deleted ${gone?.title || gone?.itemCode || "item"} — Undo (Ctrl+Z) to bring it back`,
    });
  };
  // the drawn assembly (wall/ceiling type) behind a condition: height, layers, ratings
  const asmFor = (cond) =>
    assemblies.find(
      (a) => a.id === cond.assemblyId || a.conditionId === cond.id,
    ) || null;
  const heightOf = (cond) => effectiveHeight(cond, assemblies);
  const noteLines = (txt) =>
    String(txt || "")
      .split(/\r?\n/)
      .map((l) => l.replace(/^(?:[\s•·\-–—*]+|\d{1,2}[.)]\s+)+/, "").trim())
      .filter(Boolean);
  const nextRef = () =>
    Math.max(
      0,
      ...mapRef.current.flatMap((m) =>
        (m.items || []).map((i) => Number(i.ref) || 0),
      ),
    ) + 1;
  const sameItem = (a, b) =>
    String(a.itemDatabaseId || a.itemCode) ===
      String(b.itemDatabaseId || b.itemCode) &&
    String(a.size || "") === String(b.size || "") &&
    (a.calcMode || "auto") === (b.calcMode || "auto");

  // add a catalog item — and, if it has chained items (item database → "Chained items"), those too
  const addItem = (cid, db, { chain = true } = {}) => {
    const cond = conditions.find((c) => c.id === cid);
    const mp = getMapping(cid) || { items: [] };
    const list = chain
      ? expandChain(db, catalogById)
      : [{ db, link: null, depth: 0 }];
    let ref = nextRef();
    const root = { ...itemFromCatalog(db, { cond, ccIndex }), ref: ref++ };
    const add = [root];
    const skipped = [];
    for (const { db: x, link } of list.slice(1)) {
      const it = applyLink(itemFromCatalog(x, { cond, ccIndex }), link);
      if (
        [...(mp.items || []), ...add].some(
          (e) =>
            String(e.itemDatabaseId || e.itemCode) ===
            String(it.itemDatabaseId || it.itemCode),
        )
      ) {
        skipped.push(x.itemCode);
        continue;
      }
      add.push({ ...it, ref: ref++, chainOf: root.id });
    }
    updateMapping(cid, { items: [...(mp.items || []), ...add] });
    const text = `Added ${db.itemCode}${add.length > 1 ? ` + ${add.length - 1} chained item${add.length > 2 ? "s" : ""}` : ""}${skipped.length ? ` (already there: ${skipped.join(", ")})` : ""}`;
    setMsg({ ok: true, text });
    return text;
  };

  // ── select · copy · paste ─────────────────────────────────────────────────
  const [sel, setSel] = useState(() => new Set()); // "cid|itemId"
  const lastSel = useRef(null);
  const sk = (cid, iid) => `${cid}|${iid}`;
  const selList = useMemo(() => {
    const out = [];
    for (const m of mappings)
      for (const i of m.items || [])
        if (sel.has(sk(m.conditionId, i.id)))
          out.push({ cid: m.conditionId, item: i });
    return out;
  }, [sel, mappings]);
  const toggleSel = (cid, iid, on, shift) => {
    setSel((s) => {
      const n = new Set(s);
      const items = getMapping(cid)?.items || [];
      const a =
        shift && lastSel.current?.cid === cid
          ? items.findIndex((i) => i.id === lastSel.current.iid)
          : -1;
      const b = items.findIndex((i) => i.id === iid);
      const [lo, hi] = a >= 0 && b >= 0 ? (a < b ? [a, b] : [b, a]) : [b, b];
      for (let k = lo; k <= hi && k >= 0; k++)
        (on ? n.add : n.delete).call(n, sk(cid, items[k].id));
      return n;
    });
    lastSel.current = { cid, iid };
  };
  const selectAssembly = (cid, on) =>
    setSel((s) => {
      const n = new Set(s);
      for (const i of getMapping(cid)?.items || [])
        (on ? n.add : n.delete).call(n, sk(cid, i.id));
      return n;
    });
  const clearSel = () => setSel(new Set());

  const [clip, setClip] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("est.clipboard") || "null");
    } catch {
      return null;
    }
  });
  const writeClip = (c) => {
    setClip(c);
    try {
      if (c) localStorage.setItem("est.clipboard", JSON.stringify(c));
      else localStorage.removeItem("est.clipboard");
    } catch {
      /* private mode: the clipboard still works on this page */
    }
  };
  useEffect(() => {
    // copied in another tab / another takeoff → available here too
    const h = (e) => {
      if (e.key !== "est.clipboard") return;
      try {
        setClip(JSON.parse(e.newValue || "null"));
      } catch {
        /* ignore */
      }
    };
    window.addEventListener("storage", h);
    return () => window.removeEventListener("storage", h);
  }, []);
  const condName = (cid) =>
    conditions.find((c) => c.id === cid)?.name || "assembly";
  const copyItems = (list, from) => {
    if (!list.length) return;
    writeClip({
      items: list.map((x) => JSON.parse(JSON.stringify(x))),
      from,
      at: Date.now(),
    });
    setMsg({
      ok: true,
      text: `Copied ${list.length} item${list.length === 1 ? "" : "s"} — open another assembly and Paste`,
    });
  };
  const copySelected = () => {
    const from = [...new Set(selList.map((x) => condName(x.cid)))];
    copyItems(
      selList.map((x) => x.item),
      from.length === 1 ? from[0] : `${from.length} assemblies`,
    );
  };
  const copyAssembly = (cid) =>
    copyItems(getMapping(cid)?.items || [], condName(cid));
  // paste into one or more assemblies: new ids / refs, chain links kept, items already there skipped
  const pasteInto = (cids) => {
    if (!clip?.items?.length || !cids.length) return;
    let ref = nextRef();
    let added = 0,
      skipped = 0;
    commitMappings((prev) => {
      let next = [...prev];
      for (const cid of cids) {
        const ex = next.find((m) => m.conditionId === cid);
        const have = ex?.items || [];
        const idMap = {};
        const fresh = [];
        for (const it of clip.items) {
          if (have.some((h) => sameItem(h, it))) {
            skipped++;
            continue;
          }
          idMap[it.id] = uid();
          fresh.push({ ...it, id: idMap[it.id], ref: ref++ });
        }
        for (const f of fresh)
          f.chainOf = f.chainOf ? idMap[f.chainOf] || null : null;
        added += fresh.length;
        if (!fresh.length) continue;
        if (ex)
          next = next.map((m) =>
            m.conditionId === cid ? { ...m, items: [...have, ...fresh] } : m,
          );
        else
          next.push({ conditionId: cid, bidArea: "", notes: "", items: fresh });
      }
      return next;
    });
    setMsg({
      ok: true,
      text: `Pasted ${added} item${added === 1 ? "" : "s"}${cids.length > 1 ? ` into ${cids.length} assemblies` : ""}${skipped ? ` · ${skipped} already there, skipped` : ""}`,
    });
  };
  const removeItems = (keys) => {
    const set = new Set(keys);
    commitMappings((prev) =>
      prev.map((m) =>
        (m.items || []).some((i) => set.has(sk(m.conditionId, i.id)))
          ? {
              ...m,
              items: m.items.filter((i) => !set.has(sk(m.conditionId, i.id))),
            }
          : m,
      ),
    );
    setSel((s) => new Set([...s].filter((k) => !set.has(k))));
  };
  const cutSelected = () => {
    copySelected();
    removeItems(selList.map((x) => sk(x.cid, x.item.id)));
    setMsg({
      ok: true,
      text: `Cut ${selList.length} items — Paste them into another assembly (Undo brings them back)`,
    });
  };
  const [pastePick, setPastePick] = useState(false);

  // ── waste % on many items ─────────────────────────────────────────────────
  const [wasteCtx, setWasteCtx] = useState(null); // { cid? }
  const wasteTargets = (scope, cat) => {
    const okCat = (i) =>
      cat === "all" || (cat === "L" ? i.category === "L" : i.category !== "L");
    const visible = new Set(blocks.map((b) => b.cond.id));
    const out = [];
    for (const m of mapRef.current) {
      if (scope === "assembly" && m.conditionId !== wasteCtx?.cid) continue;
      if (scope === "visible" && !visible.has(m.conditionId)) continue;
      for (const i of m.items || []) {
        if (scope === "selected" && !sel.has(sk(m.conditionId, i.id))) continue;
        if (okCat(i)) out.push(sk(m.conditionId, i.id));
      }
    }
    return out;
  };
  const applyWaste = ({ scope, cat, mode, value }) => {
    const keys = new Set(wasteTargets(scope, cat));
    const r2 = (v) => Math.round(v * 100) / 100;
    commitMappings((prev) =>
      prev.map((m) =>
        (m.items || []).some((i) => keys.has(sk(m.conditionId, i.id)))
          ? {
              ...m,
              items: m.items.map((i) =>
                keys.has(sk(m.conditionId, i.id))
                  ? {
                      ...i,
                      wastePct: Math.max(
                        0,
                        r2(
                          mode === "set"
                            ? value
                            : (Number(i.wastePct) || 0) + value,
                        ),
                      ),
                    }
                  : i,
              ),
            }
          : m,
      ),
    );
    setWasteCtx(null);
    setMsg({
      ok: true,
      text: `Waste ${mode === "set" ? `set to ${value}%` : `${value >= 0 ? "+" : ""}${value} pts`} on ${keys.size} items`,
    });
  };

  // ── standard assemblies ───────────────────────────────────────────────────
  const [libCtx, setLibCtx] = useState(null); // { target?, seed? }
  const importTemplate = (tpl, { targets, mode, refresh }) => {
    let ref = nextRef();
    commitMappings((prev) => {
      let next = [...prev];
      for (const cid of targets) {
        const items = (tpl.items || []).map((t) => {
          const db = t.itemDatabaseId
            ? catalogById.get(String(t.itemDatabaseId))
            : null;
          const it = { ...t, id: uid(), ref: ref++ };
          if (refresh && db) {
            it.rate = resolveDefaultRate(db);
            it.title = db.description || it.title;
            it.uom = db.uom || it.uom;
            if (!it.costCode && (db.costCode?._id || db.costCode))
              it.costCode = String(db.costCode?._id || db.costCode);
            if (
              it.category === "L" &&
              !(Number(it.productionRate) > 0) &&
              Number(db.productionRate) > 0
            )
              it.productionRate = Number(db.productionRate);
          }
          return it;
        });
        const ex = next.find((m) => m.conditionId === cid);
        if (ex)
          next = next.map((m) =>
            m.conditionId === cid
              ? {
                  ...m,
                  items:
                    mode === "replace" ? items : [...(m.items || []), ...items],
                  notes: m.notes || tpl.notes || "",
                  templateId: tpl._id || null,
                }
              : m,
          );
        else
          next.push({
            conditionId: cid,
            bidArea: "",
            notes: tpl.notes || "",
            items,
            templateId: tpl._id || null,
          });
      }
      return next;
    });
    const text = `Imported “${tpl.name}” into ${targets.length === 1 ? condName(targets[0]) : `${targets.length} assemblies`}`;
    setMsg({ ok: true, text });
    return text;
  };
  const saveAsStandard = (cid) => {
    const cond = conditions.find((c) => c.id === cid);
    const mp = getMapping(cid);
    const a = cond ? asmFor(cond) : null;
    setLibCtx({
      target: cid,
      seed: {
        name: cond?.name || "",
        kind: cond?.type === "area" ? "ceiling" : "wall",
        code: a?.tag || "",
        description: a?.description || noteLines(cond?.description).join("\n"),
        notes: mp?.notes || "",
        height: heightOf(cond || {}) || "",
        items: (mp?.items || []).map(toTemplateItem),
      },
    });
  };

  // ── cost codes ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!getCostCodes) return;
    getCostCodes()
      .then((r) => setCostCodes(Array.isArray(r) ? r : []))
      .catch((e) => console.warn("Cost codes load failed", e));
  }, [getCostCodes]);
  const ccLabelFor = (item) => {
    const c = resolveItemCostCode(item, { ccIndex, catalogById });
    return c.id === "uncoded"
      ? ""
      : [c.code, c.name].filter(Boolean).join(" – ");
  };
  const breakdown = useMemo(
    () => costCodeBreakdown(R, { costCodes, catalog }),
    [R, costCodes, catalog],
  );
  const [openCC, setOpenCC] = useState(() => new Set());

  // ── print ─────────────────────────────────────────────────────────────────
  const [printOpen, setPrintOpen] = useState(false);
  const [printProg, setPrintProg] = useState(null); // { title, detail, pct } while a report is being built
  const printCfg = settings.print || {};
  const patchPrint = (p) =>
    patchSettings({ print: { ...(setRef.current.print || {}), ...p } });

  const computeLegends = () =>
    buildSheetLegends({
      conditions,
      shapes,
      pageNames,
      excludedPages,
      pageBidAreas,
      mappings,
      metricsFor: (cond, pageShapes) =>
        computeCondMetrics(cond, pageShapes, scales, excludedPages, units),
    });

  // only worked out while the Print dialog is open (not on every edit to a rate / waste / item)
  const legendSheets = useMemo(
    () => (printOpen ? computeLegends() : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      printOpen,
      conditions,
      shapes,
      pageNames,
      excludedPages,
      pageBidAreas,
      scales,
      units,
    ],
  );

  const doPrint = async (opts) => {
    // open the window now (a click) — a pop-up opened after the sheets render gets blocked
    const win = openReportWindow();
    if (!win) {
      setMsg({
        ok: false,
        text: "The browser blocked the report window — allow pop-ups for this site",
      });
      return;
    }
    const step = async (title, pct, detail = "") => {
      setPrintProg({ title, detail, pct });
      reportWindowStatus(win, title, pct, detail);
      await new Promise((r) => setTimeout(r, 40)); // let both screens paint
    };
    try {
      await step("Gathering your estimate", 5);
      let legends = [];
      if ((opts.sections || []).includes("legend")) {
        legends = computeLegends(); // fresh data each print, includes the current priced items
        const want = new Set(opts.legendPages || []);
        const todo = legends.filter((s) => want.has(s.page));
        if (renderSheetImage) {
          for (let i = 0; i < todo.length; i++) {
            await step(
              `Drawing sheet ${i + 1} of ${todo.length}`,
              10 + (i / Math.max(1, todo.length)) * 78,
              todo[i].label,
            );
            try {
              todo[i].img = await renderSheetImage(todo[i].page, {
                shapes,
                conditions,
              });
            } catch (e) {
              console.warn("Legend sheet render failed", todo[i].page, e);
            }
          }
        }
      }
      await step("Building the pages", 92, "Almost there");
      const html = buildReportHtml({
        R,
        breakdown,
        legends,
        settings,
        meta: {
          company: printCfg.company || reportMeta.company || "",
          client: printCfg.client || reportMeta.client || "",
          project: reportMeta.project || ownerLabel,
          drawing: reportMeta.drawing || "",
          estimateNo: printCfg.estimateNo || reportMeta.estimateNo || "",
          preparedBy: printCfg.preparedBy || reportMeta.preparedBy || "",
          ownerKind,
          status,
        },
        options: {
          ...opts,
          inclusions: printCfg.inclusions,
          exclusions: printCfg.exclusions,
          assumptions: printCfg.assumptions,
          validDays: printCfg.validDays || 30,
        },
      });
      await step("Ready", 100, "Opening the print preview");
      fillReportWindow(win, html);
    } catch (e) {
      console.error("Print failed", e);
      try {
        win.close();
      } catch {
        /* already closed */
      }
      setMsg({ ok: false, text: e?.message || "Could not build the report" });
    } finally {
      setTimeout(() => setPrintProg(null), 400);
    }
  };

  // ── autosave ──────────────────────────────────────────────────────────────
  const [saveState, setSaveState] = useState("idle"); // idle | dirty | saving | saved | error
  const firstRun = useRef(true);
  const saveTimer = useRef(null);
  const saving = useRef(false);
  const again = useRef(false);
  const dirty = useRef(false);
  const autoRef = useRef(onAutoSave);
  autoRef.current = autoSave ? onAutoSave : null;
  const flush = async () => {
    clearTimeout(saveTimer.current);
    if (!autoRef.current) return;
    if (saving.current) {
      again.current = true;
      return;
    }
    saving.current = true;
    dirty.current = false;
    setSaveState("saving");
    try {
      await autoRef.current();
      setSaveState(dirty.current ? "dirty" : "saved");
      setSavedAt(new Date());
    } catch (e) {
      console.error("Estimate autosave failed", e);
      dirty.current = true;
      setSaveState("error");
    } finally {
      saving.current = false;
      if (again.current) {
        again.current = false;
        flush();
      }
    }
  };
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    dirty.current = true;
    setSaveState("dirty");
    if (!autoRef.current) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(flush, 1200);
  }, [mappings, settings]); // eslint-disable-line
  useEffect(() => {
    const warn = (e) => {
      if (!dirty.current && !saving.current) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => {
      window.removeEventListener("beforeunload", warn);
      clearTimeout(saveTimer.current);
      // leaving the estimate with changes not yet saved: save them now
      if (dirty.current && autoRef.current) autoRef.current().catch(() => {});
    };
  }, []);

  // ── actions ───────────────────────────────────────────────────────────────
  const run = async (key, fn) => {
    setBusy(key);
    setMsg(null);
    try {
      const out = await fn();
      return out;
    } catch (e) {
      setMsg({
        ok: false,
        text: e?.response?.data?.error || e?.message || "Something went wrong",
      });
    } finally {
      setBusy("");
    }
  };
  const saveDraft = () =>
    run("draft", async () => {
      await onSaveDraft();
      setSavedAt(new Date());
      setMsg({ ok: true, text: "Draft saved" });
    });
  const publish = () =>
    run("push", async () => {
      if (
        !window.confirm(
          "Publish this estimate to the project budget? Existing estimate rows are replaced.",
        )
      )
        return;
      const r = await onSaveAndPush();
      setMsg({
        ok: true,
        text: `Published — ${r?.created ?? 0} budget rows${r?.replaced ? `, ${r.replaced} replaced` : ""}${r?.skipped?.length ? `, ${r.skipped.length} skipped` : ""}`,
      });
    });
  const addToCo = () =>
    run("co", async () => {
      const items = [];
      blocks.forEach(({ cond, area, rows }) =>
        rows.forEach((r) => {
          if (r.qty == null || r.qty === 0) return;
          items.push(toCoItem(cond, r, { bidArea: area || "" }));
        }),
      );
      if (!items.length) throw new Error("Nothing priced to add.");
      await onAddToChangeOrder(items);
      setMsg({
        ok: true,
        text: `${items.length} lines added to the change order`,
      });
    });

  const T = R.totals;
  const hi = R.issues.filter((i) => i.sev === "high").length;
  const toggle = (id) =>
    setCollapsed((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const isBid = ownerKind === "opportunity";

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <div className="flex h-full min-h-0 flex-col bg-gray-950 text-gray-100">
      {/* header */}
      <div className="flex flex-wrap items-center gap-3 border-b border-white/10 bg-gray-900/80 px-4 py-3 backdrop-blur">
        {onBack && (
          <button
            onClick={onBack}
            className="rounded-lg bg-white/5 px-3 py-1.5 text-xs font-medium text-gray-200 hover:bg-white/10"
          >
            ← Takeoff
          </button>
        )}
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="truncate text-base font-semibold text-white">
              Estimate
            </h1>
            <span
              className={cls(
                "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
                isBid
                  ? "bg-sky-500/15 text-sky-300"
                  : "bg-orange-500/15 text-orange-300",
              )}
            >
              {isBid ? "Bid" : "Project"}
            </span>
            <span
              className={cls(
                "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
                status === "published"
                  ? "bg-emerald-500/15 text-emerald-300"
                  : "bg-amber-500/15 text-amber-300",
              )}
            >
              {status}
            </span>
          </div>
          {ownerLabel && (
            <div className="truncate text-xs text-gray-500">{ownerLabel}</div>
          )}
        </div>

        <div className="ml-auto flex items-center gap-4">
          <div className="hidden text-right sm:block">
            <div className="text-[10px] uppercase tracking-wider text-gray-500">
              Client price
            </div>
            <div className="text-lg font-bold leading-none text-orange-400">
              {money(T.price)}
            </div>
          </div>
          <div className="hidden text-right md:block">
            <div className="text-[10px] uppercase tracking-wider text-gray-500">
              Labour
            </div>
            <div className="text-sm font-semibold leading-none text-sky-300">
              {n0(T.hours)} h · {n1(T.days)} d
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex overflow-hidden rounded-lg bg-white/10">
              <button
                onClick={undo}
                disabled={!past.length}
                title="Undo (Ctrl+Z)"
                className="px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-white/10 disabled:opacity-30"
              >
                ↶ Undo
              </button>
              <button
                onClick={redo}
                disabled={!future.length}
                title="Redo (Ctrl+Shift+Z)"
                className="border-l border-white/10 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-white/10 disabled:opacity-30"
              >
                Redo ↷
              </button>
            </div>
            {autoSave && onAutoSave && (
              <button
                onClick={flush}
                title={
                  saveState === "error"
                    ? "Saving failed — click to try again"
                    : "Changes save by themselves"
                }
                className={cls(
                  "flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-[11px]",
                  saveState === "error"
                    ? "bg-red-500/15 text-red-300"
                    : "text-gray-400 hover:bg-white/5",
                )}
              >
                <span
                  className={cls(
                    "h-1.5 w-1.5 rounded-full",
                    saveState === "saving"
                      ? "animate-pulse bg-sky-400"
                      : saveState === "dirty"
                        ? "bg-amber-400"
                        : saveState === "error"
                          ? "bg-red-400"
                          : "bg-emerald-400",
                  )}
                />
                {saveState === "saving"
                  ? "Saving…"
                  : saveState === "dirty"
                    ? "Unsaved"
                    : saveState === "error"
                      ? "Save failed — retry"
                      : savedAt
                        ? `Saved ${savedAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`
                        : "All changes saved"}
              </button>
            )}
            {templateApi && (
              <button
                onClick={() => setLibCtx({})}
                className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/15"
              >
                Standard assemblies
              </button>
            )}
            <button
              onClick={() => setPrintOpen(true)}
              className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/15"
            >
              Print
            </button>
            {onToggleFullscreen && (
              <button
                onClick={onToggleFullscreen}
                title={fullscreen ? "Exit full screen" : "Full screen"}
                className="rounded-lg bg-white/10 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-white/15"
              >
                {fullscreen ? "⤡ Exit full screen" : "⤢ Full screen"}
              </button>
            )}
            {onSaveDraft && !(autoSave && onAutoSave) && (
              <button
                onClick={saveDraft}
                disabled={!!busy}
                className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/15 disabled:opacity-50"
              >
                {busy === "draft" ? "Saving…" : "Save draft"}
              </button>
            )}
            {onAddToChangeOrder && (
              <button
                onClick={addToCo}
                disabled={!!busy}
                className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/15 disabled:opacity-50"
              >
                Add to change order
              </button>
            )}
            {onSaveAndPush && !isBid && (
              <button
                onClick={publish}
                disabled={!!busy}
                className="rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-orange-400 disabled:opacity-50"
              >
                {busy === "push" ? "Publishing…" : "Publish to budget"}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* tabs */}
      <div className="flex items-center gap-1 border-b border-white/10 px-4">
        {[
          ["assemblies", "Assemblies"],
          ["pricing", "Pricing"],
          ["takelist", "Material take-list"],
          ["review", `Review${R.issues.length ? ` (${R.issues.length})` : ""}`],
        ].map(([k, l]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={cls(
              "relative px-3 py-2.5 text-[13px] font-medium",
              tab === k ? "text-white" : "text-gray-500 hover:text-gray-300",
            )}
          >
            {l}
            {k === "review" && hi > 0 && (
              <span className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-red-400" />
            )}
            {tab === k && (
              <span className="absolute inset-x-2 bottom-0 h-0.5 rounded bg-orange-500" />
            )}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2 py-1.5">
          {areas.length > 0 && (
            <select
              value={areaFilter}
              onChange={(e) => setAreaFilter(e.target.value)}
              className="rounded-lg border border-gray-700 bg-gray-900 px-2 py-1 text-xs text-gray-200"
            >
              <option value="all">All bid areas</option>
              {areas.map((a) => (
                <option key={a}>{a}</option>
              ))}
            </select>
          )}
          {msg && (
            <span
              className={cls(
                "text-xs",
                msg.ok ? "text-emerald-300" : "text-red-300",
              )}
            >
              {msg.text}
            </span>
          )}
          {!msg && savedAt && (
            <span className="text-xs text-gray-500">
              saved{" "}
              {savedAt.toLocaleTimeString([], {
                hour: "numeric",
                minute: "2-digit",
              })}
            </span>
          )}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        {/* ── ASSEMBLIES ─────────────────────────────────────────────────── */}
        {tab === "assemblies" && (
          <div className="mx-auto max-w-6xl space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <div className="inline-flex rounded-lg bg-gray-900 p-0.5 text-xs">
                {[
                  ["area", "Group by bid area"],
                  ["assembly", "By assembly"],
                ].map(([k, l]) => (
                  <button
                    key={k}
                    onClick={() => setGroupBy(k)}
                    className={cls(
                      "rounded-md px-3 py-1 font-medium",
                      groupBy === k
                        ? "bg-orange-500 text-white"
                        : "text-gray-400 hover:text-gray-200",
                    )}
                  >
                    {l}
                  </button>
                ))}
              </div>
              <div className="inline-flex rounded-lg bg-gray-900 p-0.5 text-xs">
                <button
                  onClick={() => setCollapsed(new Set())}
                  className="rounded-md px-3 py-1 font-medium text-gray-400 hover:text-gray-200"
                >
                  Expand all
                </button>
                <button
                  onClick={() =>
                    setCollapsed(new Set(R.blocks.map((b) => b.key)))
                  }
                  className="rounded-md px-3 py-1 font-medium text-gray-400 hover:text-gray-200"
                >
                  Collapse all
                </button>
              </div>
              <button
                onClick={() => setWasteCtx({})}
                className="rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-medium text-gray-300 hover:text-white"
                title="Set waste % on every assembly (or the bid area in view)"
              >
                Waste % all…
              </button>
              {templateApi && (
                <button
                  onClick={() => setLibCtx({})}
                  className="rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-medium text-gray-300 hover:text-white"
                >
                  Standard assemblies
                </button>
              )}
              {!bySheetAreas && (
                <p className="text-[11.5px] text-gray-500">
                  No bid areas are set on the drawing's sheets, so each assembly
                  uses the one typed on it. To price floors separately
                  (different heights), set a bid area on each sheet in the
                  takeoff's sheet list, then come back.
                </p>
              )}
            </div>
            {(selList.length > 0 || clip?.items?.length > 0) && (
              <div className="sticky top-0 z-20 flex flex-wrap items-center gap-2 rounded-xl border border-orange-500/30 bg-gray-900/95 px-3 py-2 shadow-lg backdrop-blur">
                {selList.length > 0 && (
                  <>
                    <span className="text-xs font-semibold text-orange-200">
                      {selList.length} selected
                    </span>
                    <button
                      onClick={copySelected}
                      className="rounded-md bg-white/10 px-2.5 py-1 text-xs text-white hover:bg-white/15"
                    >
                      Copy
                    </button>
                    <button
                      onClick={cutSelected}
                      className="rounded-md bg-white/10 px-2.5 py-1 text-xs text-white hover:bg-white/15"
                    >
                      Cut
                    </button>
                    <button
                      onClick={() => setWasteCtx({ selected: true })}
                      className="rounded-md bg-white/10 px-2.5 py-1 text-xs text-white hover:bg-white/15"
                    >
                      Waste %…
                    </button>
                    <button
                      onClick={() =>
                        setConfirmDel({
                          list: selList.map((x) => ({
                            cid: x.cid,
                            iid: x.item.id,
                          })),
                          title: `${selList.length} selected item${selList.length === 1 ? "" : "s"}`,
                        })
                      }
                      className="rounded-md bg-white/10 px-2.5 py-1 text-xs text-red-300 hover:bg-red-500/20"
                    >
                      Delete
                    </button>
                    <button
                      onClick={clearSel}
                      className="text-[11px] text-gray-400 hover:text-white"
                    >
                      Clear selection
                    </button>
                  </>
                )}
                {clip?.items?.length > 0 && (
                  <span className="ml-auto flex items-center gap-2 text-[11.5px] text-gray-400">
                    <span>
                      📋 {clip.items.length} item
                      {clip.items.length === 1 ? "" : "s"} copied
                      {clip.from ? ` from ${clip.from}` : ""}
                    </span>
                    <button
                      onClick={() => setPastePick(true)}
                      className="rounded-md bg-orange-500 px-2.5 py-1 text-xs font-semibold text-white hover:bg-orange-400"
                    >
                      Paste into…
                    </button>
                    <button
                      onClick={() => writeClip(null)}
                      className="text-gray-500 hover:text-gray-300"
                      title="Empty the clipboard"
                    >
                      ✕
                    </button>
                  </span>
                )}
              </div>
            )}
            {conditions.length === 0 && (
              <p className="py-16 text-center text-sm text-gray-500">
                No assemblies yet — trace some conditions on the drawing first.
              </p>
            )}
            {groups.map((g) => (
              <React.Fragment key={g.name ?? "all"}>
                {g.name !== null && (
                  <div className="flex flex-wrap items-center gap-4 rounded-xl border border-sky-500/20 bg-sky-500/[0.06] px-4 py-2.5">
                    <span
                      className={cls(
                        "text-sm font-semibold",
                        g.unassigned ? "text-gray-400" : "text-sky-200",
                      )}
                    >
                      {g.name}
                    </span>
                    <span className="text-[11px] text-gray-500">
                      {g.blocks.length} assembl
                      {g.blocks.length === 1 ? "y" : "ies"}
                    </span>
                    <span className="ml-auto text-xs text-orange-300">
                      Material {money(g.material)}
                    </span>
                    <span className="text-xs text-sky-300">
                      Labour {money(g.labour)} · {n0(g.hours)} h
                    </span>
                    <span className="text-sm font-bold text-white">
                      {money(g.total)}
                    </span>
                  </div>
                )}
                {g.blocks.map((b) => {
                  const { cond, metrics: m } = b;
                  const shut = collapsed.has(b.key);
                  const mix = b.total > 0 ? (b.labour / b.total) * 100 : 0;
                  return (
                    <section
                      key={b.key}
                      className="overflow-hidden rounded-2xl border border-white/10 bg-gray-900/70"
                    >
                      <div
                        onClick={(e) => {
                          if (
                            e.target.closest("input,select,button,textarea,a")
                          )
                            return;
                          toggle(b.key);
                        }}
                        className="flex cursor-pointer flex-wrap items-center gap-3 px-4 py-3 hover:bg-white/[0.02]"
                      >
                        <button
                          onClick={() => toggle(b.key)}
                          title={shut ? "Expand" : "Collapse"}
                          className="text-gray-500 hover:text-gray-200"
                        >
                          {shut ? "▸" : "▾"}
                        </button>
                        <span
                          className="h-3 w-3 shrink-0 rounded-full"
                          style={{ background: cond.color }}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 text-sm font-semibold text-white">
                            {onRenameCondition ? (
                              <input
                                value={cond.name}
                                onChange={(e) =>
                                  onRenameCondition(cond.id, e.target.value)
                                }
                                onBlur={(e) => {
                                  if (!e.target.value.trim())
                                    onRenameCondition(
                                      cond.id,
                                      "Untitled assembly",
                                    );
                                }}
                                title="Click to rename this assembly"
                                style={{
                                  width: `${Math.min(Math.max(cond.name.length + 2, 10), 48)}ch`,
                                }}
                                className="-ml-1 max-w-full rounded-md !border !border-transparent !bg-transparent !px-1 !py-0.5 text-sm font-semibold text-white !shadow-none !outline-none !ring-0 hover:!border-white/15 focus:!border-orange-500/60 focus:!bg-gray-950 focus:!ring-0"
                              />
                            ) : (
                              <span className="truncate">{cond.name}</span>
                            )}
                            {bySheetAreas && (
                              <span className="rounded-md bg-sky-500/15 px-2 py-0.5 text-[11px] font-semibold text-sky-300">
                                {b.area || "Unassigned sheets"}
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-x-2 text-[11px] text-gray-500">
                            {(b.height || heightOf(cond)) > 0 && (
                              <span className="rounded bg-orange-500/15 px-1.5 py-px font-semibold text-orange-300">
                                H{" "}
                                {n1(
                                  b.height || heightOf(cond),
                                  (b.height || heightOf(cond)) % 1 ? 1 : 0,
                                )}
                                ′
                                {b.heights?.length > 1
                                  ? ` – ${n1(Math.max(...b.heights), Math.max(...b.heights) % 1 ? 1 : 0)}′ mixed`
                                  : ""}
                              </span>
                            )}
                            {asmFor(cond)?.fireRating && (
                              <span className="rounded bg-red-500/15 px-1.5 py-px text-red-300">
                                {asmFor(cond).fireRating}
                              </span>
                            )}
                            {asmFor(cond)?.stc && (
                              <span className="rounded bg-white/10 px-1.5 py-px text-gray-300">
                                STC {asmFor(cond).stc}
                              </span>
                            )}
                            {m?.uncal
                              ? "⚠ sheet scale not set"
                              : [
                                  m?.sf > 0 && `${n0(m.sf)} SF`,
                                  m?.lf > 0 && `${n0(m.lf)} LF`,
                                  m?.count > 0 && `${n0(m.count)} EA`,
                                ]
                                  .filter(Boolean)
                                  .join(" · ") || "no quantity traced"}
                          </div>
                        </div>
                        {!bySheetAreas && (
                          <input
                            list="bid-areas"
                            value={b.mapping?.bidArea || ""}
                            onChange={(e) =>
                              updateMapping(
                                cond.id,
                                { bidArea: e.target.value },
                                "bid:" + cond.id,
                              )
                            }
                            placeholder="Bid area"
                            title="Tip: set a bid area on each sheet in the takeoff to price floors separately"
                            className={cls(inp, "w-32")}
                          />
                        )}
                        <div className="hidden w-28 sm:block">
                          <div className="mb-1 flex h-1.5 overflow-hidden rounded-full bg-gray-800">
                            <div
                              style={{
                                width: `${100 - mix}%`,
                                background: MATERIAL,
                              }}
                            />
                            <div
                              style={{ width: `${mix}%`, background: LABOUR }}
                            />
                          </div>
                          <div className="text-[10px] text-gray-500">
                            {n0(b.hours)} h
                          </div>
                        </div>
                        <div className="w-24 text-right text-sm font-bold text-white">
                          {money(b.total)}
                        </div>
                      </div>
                      {!shut && (
                        <>
                          {(() => {
                            const lines = noteLines(cond.description);
                            const a = asmFor(cond);
                            const layers = (a?.layers || [])
                              .map((l) =>
                                typeof l === "string"
                                  ? l
                                  : l?.name ||
                                    l?.description ||
                                    l?.material ||
                                    "",
                              )
                              .filter(Boolean);
                            const priced = (b.mapping?.items || []).length;
                            if (
                              !lines.length &&
                              !layers.length &&
                              !a?.description
                            )
                              return null;
                            return (
                              <div className="border-t border-white/5 bg-sky-500/[0.04] px-4 py-3">
                                <div className="mb-1.5 flex items-center gap-2">
                                  <span className="text-[10px] font-semibold uppercase tracking-wider text-sky-300">
                                    Assembly notes from the drawings
                                  </span>
                                  {heightOf(cond) > 0 && (
                                    <span className="text-[10px] text-gray-500">
                                      · height{" "}
                                      {n1(
                                        heightOf(cond),
                                        heightOf(cond) % 1 ? 1 : 0,
                                      )}
                                      ′
                                      {Number(cond.height) > 0
                                        ? " is used for wall SF"
                                        : " (from notes — set it on the condition to use it)"}
                                    </span>
                                  )}
                                  <span className="ml-auto text-[10px] text-gray-500">
                                    {priced} item{priced === 1 ? "" : "s"}{" "}
                                    priced
                                  </span>
                                </div>
                                {a?.description && !lines.length && (
                                  <p className="mb-1 text-[12px] text-gray-300">
                                    {a.description}
                                  </p>
                                )}
                                <ul className="space-y-1">
                                  {(lines.length ? lines : layers).map(
                                    (l, i) => (
                                      <li
                                        key={i}
                                        className="flex items-center gap-2 text-[12.5px] text-gray-200"
                                      >
                                        <span className="h-1 w-1 shrink-0 rounded-full bg-sky-400" />
                                        <span className="min-w-0 flex-1">
                                          {l}
                                        </span>
                                        <button
                                          onClick={() =>
                                            setSearchFor({ id: cond.id, q: l })
                                          }
                                          className="shrink-0 rounded-md bg-white/5 px-2 py-0.5 text-[10.5px] text-orange-300 hover:bg-orange-500/20"
                                        >
                                          + Price this
                                        </button>
                                      </li>
                                    ),
                                  )}
                                </ul>
                              </div>
                            );
                          })()}
                          <div className="hidden grid-cols-12 gap-2 border-t border-white/5 bg-black/20 px-3 py-1.5 text-[9.5px] font-semibold uppercase tracking-wider text-gray-500 md:grid">
                            <div className="col-span-4 flex items-center gap-2">
                              {(b.mapping?.items || []).length > 0 && (
                                <input
                                  type="checkbox"
                                  title="Select every item in this assembly"
                                  checked={(b.mapping?.items || []).every((i) =>
                                    sel.has(sk(cond.id, i.id)),
                                  )}
                                  onChange={(e) =>
                                    selectAssembly(cond.id, e.target.checked)
                                  }
                                  className="h-3.5 w-3.5 cursor-pointer accent-orange-500"
                                />
                              )}
                              <span className="pl-4">Item</span>
                            </div>
                            <div className="col-span-3">Basis</div>
                            <div className="col-span-1 text-right">Qty</div>
                            <div className="col-span-1 text-right">Hours</div>
                            <div className="col-span-1 text-right">Rate</div>
                            <div className="col-span-1 text-right">Amount</div>
                            <div className="col-span-1" />
                          </div>
                          {b.rows.map((r) => (
                            <ItemRow
                              key={r.item.id}
                              row={r}
                              cond={
                                b.height ? { ...cond, height: b.height } : cond
                              }
                              onChange={(p) =>
                                updateItem(cond.id, r.item.id, p)
                              }
                              refNo={r.item.ref}
                              selected={sel.has(sk(cond.id, r.item.id))}
                              onSelect={(on, shift) =>
                                toggleSel(cond.id, r.item.id, on, shift)
                              }
                              ccLabel={ccLabelFor(r.item)}
                              ccOptions={ccOptions}
                              chainedFrom={
                                r.item.chainOf
                                  ? (() => {
                                      const p = (b.mapping?.items || []).find(
                                        (i) => i.id === r.item.chainOf,
                                      );
                                      return p ? p.itemCode || p.title : "";
                                    })()
                                  : ""
                              }
                              onRemove={() => {
                                const kids = (b.mapping?.items || []).filter(
                                  (i) => i.chainOf === r.item.id,
                                );
                                setConfirmDel({
                                  list: [{ cid: cond.id, iid: r.item.id }],
                                  kids: kids.map((i) => ({
                                    cid: cond.id,
                                    iid: i.id,
                                    title: i.title || i.itemCode,
                                  })),
                                  withKids: true,
                                  title: r.item.title || r.item.itemCode,
                                  ref: r.item.ref,
                                });
                              }}
                            />
                          ))}
                          <div className="flex flex-wrap items-center gap-3 border-t border-white/5 px-4 py-2.5">
                            <button
                              onClick={() =>
                                setSearchFor({ id: cond.id, q: "" })
                              }
                              className="rounded-lg bg-orange-500/15 px-3 py-1.5 text-xs font-semibold text-orange-300 hover:bg-orange-500/25"
                            >
                              + Add item from database
                            </button>
                            {clip?.items?.length > 0 && (
                              <button
                                onClick={() => pasteInto([cond.id])}
                                title={`Paste the ${clip.items.length} copied items${clip.from ? ` from ${clip.from}` : ""}`}
                                className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/15"
                              >
                                Paste {clip.items.length}
                              </button>
                            )}
                            {templateApi && (
                              <button
                                onClick={() => setLibCtx({ target: cond.id })}
                                className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/15"
                              >
                                Import standard
                              </button>
                            )}
                            <Menu
                              label="More ▾"
                              items={[
                                (b.mapping?.items || []).length > 0 && [
                                  "Copy all items",
                                  () => copyAssembly(cond.id),
                                ],
                                (b.mapping?.items || []).length > 0 && [
                                  "Waste % on this assembly…",
                                  () => setWasteCtx({ cid: cond.id }),
                                ],
                                templateApi &&
                                  (b.mapping?.items || []).length > 0 && [
                                    "Save as standard assembly…",
                                    () => saveAsStandard(cond.id),
                                  ],
                                (b.mapping?.items || []).length > 0 && [
                                  "Remove all items",
                                  () =>
                                    setConfirmDel({
                                      list: (b.mapping?.items || []).map(
                                        (i) => ({ cid: cond.id, iid: i.id }),
                                      ),
                                      title: `all ${(b.mapping?.items || []).length} items of ${cond.name}`,
                                    }),
                                  true,
                                ],
                              ]}
                            />
                            <input
                              value={b.mapping?.notes || ""}
                              onChange={(e) =>
                                updateMapping(
                                  cond.id,
                                  { notes: e.target.value },
                                  "notes:" + cond.id,
                                )
                              }
                              placeholder="Scope notes (shown on the proposal summary)…"
                              className={cls(inp, "min-w-[12rem] flex-1")}
                            />
                            <div className="ml-auto flex gap-4 text-xs">
                              <span className="text-orange-300">
                                Material {money(b.material)}
                              </span>
                              <span className="text-sky-300">
                                Labour {money(b.labour)}
                              </span>
                            </div>
                          </div>
                        </>
                      )}
                    </section>
                  );
                })}
              </React.Fragment>
            ))}
            <datalist id="bid-areas">
              {areas.map((a) => (
                <option key={a} value={a} />
              ))}
            </datalist>
          </div>
        )}

        {/* ── PRICING ────────────────────────────────────────────────────── */}
        {tab === "pricing" && (
          <div className="mx-auto grid max-w-6xl gap-4 lg:grid-cols-5">
            {/* donut + KPIs */}
            <div className="rounded-2xl border border-white/10 bg-gray-900/70 p-5 lg:col-span-2">
              <h3 className="mb-4 text-xs font-semibold uppercase tracking-wider text-gray-400">
                Cost split
              </h3>
              <div className="flex flex-col items-center gap-5">
                <Donut
                  parts={[
                    { label: "Material", value: T.material, color: MATERIAL },
                    { label: "Labour", value: T.labour, color: LABOUR },
                  ]}
                  center={
                    <>
                      <div className="text-[10px] uppercase tracking-wider text-gray-500">
                        Direct cost
                      </div>
                      <div className="text-xl font-bold text-white">
                        {money(T.direct)}
                      </div>
                    </>
                  }
                />
                <div className="grid w-full grid-cols-2 gap-3">
                  {[
                    ["Material", T.material, MATERIAL],
                    ["Labour", T.labour, LABOUR],
                  ].map(([l, v, c]) => (
                    <div key={l} className="rounded-xl bg-black/25 p-3">
                      <div className="flex items-center gap-2 text-[11px] text-gray-400">
                        <span
                          className="h-2 w-2 rounded-full"
                          style={{ background: c }}
                        />
                        {l}
                      </div>
                      <div className="mt-1 text-lg font-semibold text-white">
                        {money(v)}
                      </div>
                      <div className="text-[11px] text-gray-500">
                        {T.direct > 0 ? ((v / T.direct) * 100).toFixed(1) : 0}%
                      </div>
                    </div>
                  ))}
                  <div className="rounded-xl bg-black/25 p-3">
                    <div className="text-[11px] text-gray-400">
                      Labour hours
                    </div>
                    <div className="mt-1 text-lg font-semibold text-sky-300">
                      {n0(T.hours)} h
                    </div>
                  </div>
                  <div className="rounded-xl bg-black/25 p-3">
                    <div className="text-[11px] text-gray-400">
                      Crew-days ({settings.crewSize ?? 4} ×{" "}
                      {settings.hoursPerDay ?? 8}h)
                    </div>
                    <div className="mt-1 text-lg font-semibold text-sky-300">
                      {n1(T.days)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* price build-up */}
            <div className="rounded-2xl border border-white/10 bg-gray-900/70 p-5 lg:col-span-3">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                  Price build-up
                </h3>
                <button
                  onClick={() => setPrintOpen(true)}
                  className="rounded-lg bg-white/5 px-2.5 py-1 text-[11px] text-gray-300 hover:bg-white/10"
                >
                  Print / PDF
                </button>
              </div>
              <div className="space-y-1.5 text-sm">
                {[
                  ["Material", T.material, null],
                  ["Labour", T.labour, null],
                  ["Direct cost", T.direct, "strong"],
                  ["Overhead", T.overhead, "overheadPct"],
                  ["Profit", T.profit, "profitPct"],
                  ["Contingency", T.contingency, "contingencyPct"],
                  ["Subtotal", T.subtotal, "strong"],
                  ["Tax", T.tax, "taxPct"],
                ].map(([l, v, k]) => (
                  <div
                    key={l}
                    className={cls(
                      "flex items-center gap-3",
                      k === "strong" &&
                        "border-t border-white/10 pt-1.5 font-semibold text-white",
                    )}
                  >
                    <span className="flex-1 text-gray-300">{l}</span>
                    {k && k !== "strong" && (
                      <span className="flex items-center gap-1">
                        <input
                          type="number"
                          step="any"
                          value={settings[k] ?? ""}
                          onChange={(e) =>
                            patchSettings({
                              [k]:
                                e.target.value === ""
                                  ? ""
                                  : Number(e.target.value),
                            })
                          }
                          className={cls(inp, "w-16 text-right")}
                        />
                        <span className="text-xs text-gray-500">%</span>
                      </span>
                    )}
                    <span className="w-28 text-right tabular-nums">
                      {money2(v)}
                    </span>
                  </div>
                ))}
                <div className="mt-2 flex items-center justify-between rounded-xl bg-orange-500/10 px-4 py-3">
                  <span className="text-sm font-semibold text-orange-200">
                    Price to client
                  </span>
                  <span className="text-2xl font-bold text-orange-400 tabular-nums">
                    {money2(T.price)}
                  </span>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-3 border-t border-white/10 pt-4">
                <Field label="Crew size">
                  <input
                    type="number"
                    min="1"
                    value={settings.crewSize ?? ""}
                    onChange={(e) =>
                      patchSettings({
                        crewSize:
                          e.target.value === "" ? "" : Number(e.target.value),
                      })
                    }
                    className={cls(inp, "w-20")}
                  />
                </Field>
                <Field label="Hours / day">
                  <input
                    type="number"
                    min="1"
                    value={settings.hoursPerDay ?? ""}
                    onChange={(e) =>
                      patchSettings({
                        hoursPerDay:
                          e.target.value === "" ? "" : Number(e.target.value),
                      })
                    }
                    className={cls(inp, "w-20")}
                  />
                </Field>
                <div className="self-end text-[11px] text-gray-500">
                  Overhead is on direct cost; profit is on cost + overhead;
                  contingency is on direct cost.
                </div>
              </div>
            </div>

            {/* by cost code */}
            <div className="rounded-2xl border border-white/10 bg-gray-900/70 p-5 lg:col-span-5">
              <div className="mb-3 flex items-center gap-3">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                  By cost code
                </h3>
                <span className="text-[11px] text-gray-500">
                  from each item's cost code (set on the item, else the item
                  database's, else its section)
                </span>
                {breakdown.length > 0 && (
                  <button
                    onClick={() =>
                      setOpenCC((s) =>
                        s.size
                          ? new Set()
                          : new Set(breakdown.map((g) => g.id)),
                      )
                    }
                    className="ml-auto text-[11px] text-gray-400 hover:text-white"
                  >
                    {openCC.size ? "Collapse all" : "Show items"}
                  </button>
                )}
              </div>
              {breakdown.length === 0 ? (
                <p className="py-6 text-center text-sm text-gray-500">
                  Nothing priced yet.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[720px] text-sm">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500">
                        <th className="pb-2 font-semibold">Cost code</th>
                        <th className="pb-2 text-right font-semibold">
                          Material
                        </th>
                        <th className="pb-2 text-right font-semibold">
                          Labour
                        </th>
                        <th className="pb-2 text-right font-semibold">Hours</th>
                        <th className="pb-2 text-right font-semibold">
                          Direct
                        </th>
                        <th className="pb-2 text-right font-semibold">Share</th>
                        <th className="pb-2 text-right font-semibold">
                          Client price
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {breakdown.map((g) => {
                        const open = openCC.has(g.id);
                        const share = T.direct > 0 ? g.total / T.direct : 0;
                        return (
                          <React.Fragment key={g.id}>
                            <tr
                              onClick={() =>
                                setOpenCC((s) => {
                                  const n = new Set(s);
                                  n.has(g.id) ? n.delete(g.id) : n.add(g.id);
                                  return n;
                                })
                              }
                              className="cursor-pointer border-t border-white/5 hover:bg-white/[0.03]"
                            >
                              <td className="py-2 pr-3">
                                <div className="flex items-center gap-2">
                                  <span className="w-3 text-[10px] text-gray-500">
                                    {open ? "▾" : "▸"}
                                  </span>
                                  <span
                                    className={cls(
                                      "rounded px-1.5 py-px font-mono text-[11px]",
                                      g.id === "uncoded"
                                        ? "bg-red-500/15 text-red-300"
                                        : "bg-violet-500/15 text-violet-200",
                                    )}
                                  >
                                    {g.code}
                                  </span>
                                  <span className="truncate text-gray-200">
                                    {g.name}
                                  </span>
                                  <span className="text-[10px] text-gray-600">
                                    {g.lines.length} items
                                  </span>
                                </div>
                              </td>
                              <td className="py-2 text-right tabular-nums text-orange-200">
                                {money(g.material)}
                              </td>
                              <td className="py-2 text-right tabular-nums text-sky-200">
                                {money(g.labour)}
                              </td>
                              <td className="py-2 text-right tabular-nums text-gray-300">
                                {n0(g.hours)}
                              </td>
                              <td className="py-2 text-right tabular-nums text-gray-100">
                                {money(g.total)}
                              </td>
                              <td className="w-28 py-2 pl-3">
                                <div className="flex items-center gap-2">
                                  <div className="h-1.5 flex-1 rounded-full bg-gray-800">
                                    <div
                                      className="h-1.5 rounded-full bg-violet-400"
                                      style={{ width: `${share * 100}%` }}
                                    />
                                  </div>
                                  <span className="w-9 text-right text-[10px] text-gray-500">
                                    {(share * 100).toFixed(0)}%
                                  </span>
                                </div>
                              </td>
                              <td className="py-2 text-right font-semibold tabular-nums text-white">
                                {money(g.price)}
                              </td>
                            </tr>
                            {open &&
                              g.lines.map((l, k) => (
                                <tr key={k} className="bg-black/20 text-[12px]">
                                  <td className="py-1 pl-9 pr-3 text-gray-400">
                                    <span className="mr-1.5 font-mono text-[10px] text-gray-600">
                                      {l.ref != null
                                        ? `#${String(l.ref).padStart(3, "0")}`
                                        : ""}
                                    </span>
                                    {l.title || l.itemCode}
                                    <span className="text-gray-600">
                                      {" "}
                                      · {l.assembly}
                                      {l.area ? ` (${l.area})` : ""} ·{" "}
                                      {n1(l.qty, l.qty % 1 ? 1 : 0)} {l.unit}
                                    </span>
                                  </td>
                                  <td className="py-1 text-right tabular-nums text-orange-200/70">
                                    {l.category === "M" ? money(l.amount) : ""}
                                  </td>
                                  <td className="py-1 text-right tabular-nums text-sky-200/70">
                                    {l.category === "L" ? money(l.amount) : ""}
                                  </td>
                                  <td className="py-1 text-right tabular-nums text-gray-500">
                                    {l.hours ? n1(l.hours) : ""}
                                  </td>
                                  <td className="py-1 text-right tabular-nums text-gray-400">
                                    {money(l.amount)}
                                  </td>
                                  <td />
                                  <td />
                                </tr>
                              ))}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="border-t border-white/15 font-semibold">
                        <td className="py-2 text-gray-300">Total</td>
                        <td className="py-2 text-right tabular-nums text-orange-200">
                          {money(T.material)}
                        </td>
                        <td className="py-2 text-right tabular-nums text-sky-200">
                          {money(T.labour)}
                        </td>
                        <td className="py-2 text-right tabular-nums text-gray-300">
                          {n0(T.hours)}
                        </td>
                        <td className="py-2 text-right tabular-nums text-gray-100">
                          {money(T.direct)}
                        </td>
                        <td />
                        <td className="py-2 text-right tabular-nums text-white">
                          {money(T.price)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>

            {/* by assembly */}
            <div className="rounded-2xl border border-white/10 bg-gray-900/70 p-5 lg:col-span-5">
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-400">
                By assembly
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500">
                      <th className="pb-2 font-semibold">Assembly</th>
                      <th className="pb-2 font-semibold">Mix</th>
                      <th className="pb-2 text-right font-semibold">
                        Material
                      </th>
                      <th className="pb-2 text-right font-semibold">Labour</th>
                      <th className="pb-2 text-right font-semibold">Hours</th>
                      <th className="pb-2 text-right font-semibold">Direct</th>
                      <th className="pb-2 text-right font-semibold">
                        Client price
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...blocks]
                      .sort((a, b) => b.total - a.total)
                      .map((b) => {
                        const share = T.direct > 0 ? b.total / T.direct : 0;
                        const mix =
                          b.total > 0 ? (b.labour / b.total) * 100 : 0;
                        const markup = T.direct > 0 ? T.subtotal / T.direct : 1;
                        return (
                          <tr
                            key={b.cond.id}
                            className="border-t border-white/5"
                          >
                            <td className="py-2 pr-3">
                              <div className="flex items-center gap-2">
                                <span
                                  className="h-2.5 w-2.5 rounded-full"
                                  style={{ background: b.cond.color }}
                                />
                                <span className="text-gray-100">
                                  {b.cond.name}
                                </span>
                                <span className="text-[10px] text-gray-600">
                                  {(share * 100).toFixed(0)}%
                                </span>
                              </div>
                              <div
                                className="mt-1 h-1 rounded-full bg-gray-800"
                                style={{ width: "100%" }}
                              >
                                <div
                                  className="h-1 rounded-full"
                                  style={{
                                    width: `${share * 100}%`,
                                    background: b.cond.color,
                                  }}
                                />
                              </div>
                            </td>
                            <td className="w-28 py-2 pr-3">
                              <div className="flex h-2 overflow-hidden rounded-full bg-gray-800">
                                <div
                                  style={{
                                    width: `${100 - mix}%`,
                                    background: MATERIAL,
                                  }}
                                />
                                <div
                                  style={{
                                    width: `${mix}%`,
                                    background: LABOUR,
                                  }}
                                />
                              </div>
                            </td>
                            <td className="py-2 text-right tabular-nums text-orange-200">
                              {money(b.material)}
                            </td>
                            <td className="py-2 text-right tabular-nums text-sky-200">
                              {money(b.labour)}
                            </td>
                            <td className="py-2 text-right tabular-nums text-gray-300">
                              {n0(b.hours)}
                            </td>
                            <td className="py-2 text-right tabular-nums text-gray-100">
                              {money(b.total)}
                            </td>
                            <td className="py-2 text-right font-semibold tabular-nums text-white">
                              {money(
                                b.total *
                                  markup *
                                  (1 + (R.settings.taxPct || 0) / 100),
                              )}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* by bid area */}
            {R.byArea.length > 1 ||
            (R.byArea[0] && R.byArea[0].area !== "Unassigned") ? (
              <div className="rounded-2xl border border-white/10 bg-gray-900/70 p-5 lg:col-span-5">
                <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-400">
                  By bid area
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[560px] text-sm">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500">
                        <th className="pb-2 font-semibold">Area</th>
                        <th className="pb-2 text-right font-semibold">
                          Material
                        </th>
                        <th className="pb-2 text-right font-semibold">
                          Labour
                        </th>
                        <th className="pb-2 text-right font-semibold">Hours</th>
                        <th className="pb-2 text-right font-semibold">
                          Direct
                        </th>
                        <th className="pb-2 text-right font-semibold">
                          Client price
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {R.byArea.map((a) => {
                        const markup = T.direct > 0 ? T.subtotal / T.direct : 1;
                        return (
                          <tr key={a.area} className="border-t border-white/5">
                            <td className="py-2 text-gray-100">{a.area}</td>
                            <td className="py-2 text-right tabular-nums text-orange-200">
                              {money(a.material)}
                            </td>
                            <td className="py-2 text-right tabular-nums text-sky-200">
                              {money(a.labour)}
                            </td>
                            <td className="py-2 text-right tabular-nums text-gray-300">
                              {n0(a.hours)}
                            </td>
                            <td className="py-2 text-right tabular-nums text-gray-100">
                              {money(a.total)}
                            </td>
                            <td className="py-2 text-right font-semibold tabular-nums text-white">
                              {money(
                                a.total *
                                  markup *
                                  (1 + (R.settings.taxPct || 0) / 100),
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </div>
        )}

        {/* ── TAKE-LIST + VENDOR PRICING ─────────────────────────────────── */}
        {tab === "takelist" && (
          <VendorPricing
            rows={R.takeList}
            mappings={mappings}
            catalogById={catalogById}
            ccIndex={ccIndex}
            vendorPricing={settings.vendorPricing}
            setVendorPricing={(v) => patchSettings({ vendorPricing: v })}
            onApply={applyVendorPrices}
            takeoffId={takeoffId}
            ownerKind={ownerKind}
            ownerId={ownerId}
            jobName={reportMeta.project || ownerLabel}
            companyName={reportMeta.company || ""}
            apiBase={apiBase}
            n0={n0}
          />
        )}

        {/* ── REVIEW ─────────────────────────────────────────────────────── */}
        {tab === "review" && (
          <div className="mx-auto max-w-4xl space-y-2">
            {R.issues.length === 0 ? (
              <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-8 text-center text-sm text-emerald-300">
                ✓ Nothing flagged — every assembly has quantities, rates and
                production rates.
              </div>
            ) : (
              R.issues.map((i, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setTab("assemblies");
                    setAreaFilter("all");
                    setCollapsed(new Set());
                  }}
                  className={cls(
                    "flex w-full items-start gap-3 rounded-xl border px-4 py-3 text-left text-sm",
                    i.sev === "high"
                      ? "border-red-700/50 bg-red-950/30 text-red-100"
                      : i.sev === "med"
                        ? "border-yellow-700/40 bg-yellow-950/20 text-yellow-100"
                        : "border-white/10 bg-gray-900/60 text-gray-300",
                  )}
                >
                  <span className="mt-0.5 text-[10px] font-bold uppercase">
                    {i.sev === "high"
                      ? "Fix"
                      : i.sev === "med"
                        ? "Check"
                        : "Note"}
                  </span>
                  <span className="flex-1">
                    {i.title}
                    {i.detail ? (
                      <span className="block text-xs opacity-70">
                        {i.detail}
                      </span>
                    ) : null}
                  </span>
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {confirmDel && (
        <div
          className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/60 p-4"
          onClick={() => setConfirmDel(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm rounded-2xl border border-white/10 bg-gray-900 p-5 shadow-2xl"
          >
            <h3 className="text-base font-semibold text-white">
              {confirmDel.list?.length > 1
                ? "Delete these items?"
                : "Delete this item?"}
            </h3>
            <p className="mt-2 text-sm text-gray-300">
              {confirmDel.ref != null && (
                <span className="mr-1.5 rounded bg-white/5 px-1.5 py-px font-mono text-xs text-gray-400">
                  #{String(confirmDel.ref).padStart(3, "0")}
                </span>
              )}
              <span className="font-medium text-white">{confirmDel.title}</span>{" "}
              will be removed. You can undo it afterwards.
            </p>
            {confirmDel.kids?.length > 0 && (
              <label className="mt-3 flex cursor-pointer items-start gap-2 rounded-lg bg-amber-500/10 px-3 py-2 text-[12.5px] text-amber-100">
                <input
                  type="checkbox"
                  checked={!!confirmDel.withKids}
                  onChange={(e) =>
                    setConfirmDel((c) => ({ ...c, withKids: e.target.checked }))
                  }
                  className="mt-0.5 accent-orange-500"
                />
                <span>
                  Also delete the {confirmDel.kids.length} item
                  {confirmDel.kids.length === 1 ? "" : "s"} that came in with it
                  (⛓{" "}
                  {confirmDel.kids
                    .slice(0, 3)
                    .map((k) => k.title)
                    .join(", ")}
                  {confirmDel.kids.length > 3 ? "…" : ""})
                </span>
              </label>
            )}
            <div className="mt-5 flex justify-end gap-2">
              <button
                autoFocus
                onClick={() => setConfirmDel(null)}
                className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/15"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const list = [
                    ...(confirmDel.list || []),
                    ...(confirmDel.withKids ? confirmDel.kids || [] : []),
                  ];
                  if (list.length === 1) removeItem(list[0].cid, list[0].iid);
                  else {
                    removeItems(list.map((x) => sk(x.cid, x.iid)));
                    setMsg({
                      ok: true,
                      text: `Deleted ${list.length} items — Undo (Ctrl+Z) to bring them back`,
                    });
                  }
                  setConfirmDel(null);
                }}
                className="rounded-lg bg-red-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-400"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {printProg && (
        <div className="fixed inset-0 z-[2500] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-gray-900 p-6 text-center shadow-2xl">
            <div className="relative mx-auto mb-4 h-14 w-14">
              <span className="absolute inset-0 animate-spin rounded-full border-4 border-transparent border-t-orange-500" />
              <span
                className="absolute inset-2 rounded-full border-4 border-transparent border-t-sky-400"
                style={{
                  animation: "spin 1.5s linear infinite reverse",
                }}
              />
            </div>
            <div className="text-sm font-semibold text-white">
              {printProg.title}
            </div>
            <div className="mt-0.5 min-h-[16px] truncate text-[12px] text-gray-400">
              {printProg.detail || "Hang tight — this opens in a new window"}
            </div>
            <div className="mt-4 h-2 overflow-hidden rounded-full bg-gray-800">
              <div
                className="h-full rounded-full bg-gradient-to-r from-orange-500 to-orange-400 transition-all duration-500"
                style={{ width: `${Math.max(4, printProg.pct || 0)}%` }}
              />
            </div>
            <div className="mt-1.5 text-[11px] tabular-nums text-gray-500">
              {Math.round(printProg.pct || 0)}%
            </div>
          </div>
        </div>
      )}

      <ItemSearchModal
        open={!!searchFor}
        initialQ={searchFor?.q || ""}
        onClose={() => setSearchFor(null)}
        onSelect={(db, o) => addItem(searchFor.id, db, o)}
        catalog={catalog}
        loading={catLoading}
        sub={searchFor ? `into ${condName(searchFor.id)}` : ""}
      />
      <WasteModal
        open={!!wasteCtx}
        onClose={() => setWasteCtx(null)}
        defaultScope={
          wasteCtx?.cid
            ? "assembly"
            : wasteCtx?.selected
              ? "selected"
              : areaFilter !== "all"
                ? "visible"
                : "all"
        }
        scopes={[
          wasteCtx?.cid && {
            key: "assembly",
            label: `This assembly — ${condName(wasteCtx.cid)}`,
          },
          selList.length > 0 && {
            key: "selected",
            label: `The ${selList.length} selected item${selList.length === 1 ? "" : "s"}`,
          },
          areaFilter !== "all" && {
            key: "visible",
            label: `Every assembly in bid area “${areaFilter}”`,
          },
          {
            key: "all",
            label: `Every assembly (${mappings.filter((m) => (m.items || []).length).length})`,
          },
        ].filter(Boolean)}
        countFor={(scope, cat) => wasteTargets(scope, cat).length}
        onApply={applyWaste}
      />
      {templateApi && (
        <LibraryModal
          open={!!libCtx}
          onClose={() => setLibCtx(null)}
          api={templateApi}
          catalog={catalog}
          catLoading={catLoading}
          catalogById={catalogById}
          ccIndex={ccIndex}
          conditions={conditions.map((c) => ({
            id: c.id,
            name: c.name,
            color: c.color,
            itemCount: (getMapping(c.id)?.items || []).length,
          }))}
          target={libCtx?.target || null}
          seed={libCtx?.seed || null}
          onImport={importTemplate}
        />
      )}
      <AssemblyPicker
        open={pastePick}
        onClose={() => setPastePick(false)}
        title="Paste into assemblies"
        sub={
          clip
            ? `${clip.items?.length || 0} items${clip.from ? ` copied from ${clip.from}` : ""} · items already in an assembly are skipped`
            : ""
        }
        conditions={conditions.map((c) => ({
          id: c.id,
          name: c.name,
          color: c.color,
          itemCount: (getMapping(c.id)?.items || []).length,
        }))}
        onPick={pasteInto}
      />
      <PrintModal
        open={printOpen}
        onClose={() => setPrintOpen(false)}
        print={printCfg}
        defaults={reportMeta}
        sheets={legendSheets.map((s) => ({ page: s.page, label: s.label }))}
        onChange={patchPrint}
        onPrint={doPrint}
        onPrintDrawings={
          printDrawings
            ? (pages) => {
                setPrintOpen(false);
                printDrawings(pages); // same one-PDF output as the drawing's Print button
              }
            : null
        }
      />
    </div>
  );
}
